import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BonusService } from 'src/app/services/bonus/bonus.service';
import { EventsService } from 'src/app/services/event/event.service';
import { SuccessMessageService } from 'src/app/services/message-provider/success-message.service';
import { Navigator } from "src/app/services/routing/navigator";
import { ConfigService } from 'src/app/services/config/config.service';
import { AccountBalanceService } from 'src/app/services/balance/account-balance.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { AppLinksService } from 'src/app/services/applinks/app-links.service';
import { GameHubControllerService } from 'src/app/services/gameHub/game-hub-controller';
import type { PopoverSize } from '@ionic/core';
import { NavParams } from '@ionic/angular';
import { RoutingService } from 'src/app/services/routing/routing.service';
import { StructureHelperService } from 'src/app/services/structure/structure-helper.service';

declare function parseFloat(params: string | number);

export interface IBonusVertical {
  rolloverWagerMultiplier: number;
  wagerContribution: number;
  wagersRemaining: number;
}

export interface IBonus {
  id: string;
  displayName: string;
  status: string // 'FORFEITED' | 'RELEASED';
  expiryDate: string;
  grantedDate: string;
  percentContributedCasino: number;
  percentContributedSports: number;
  currentBonus: number;
  campaignId?: number;
  originalBonus: number;
  currentDeposit: number;
  originalDeposit: number
  currentWinning: number;
  scope?: any[];
  vertical: { [key: string]: IBonusVertical } | null; // 'CASINO'|'SPORTS'
}

@Component({
  selector: 'app-bonus-details',
  templateUrl: './bonus-details.component.html',
  styleUrls: ['./bonus-details.component.scss'],
})
export class BonusDetailsComponent implements OnInit {
  @Input() bonus: IBonus;
  @Input() source;
  @Input() hideBack;

  error = null;
  sportsEnabled = false;
  casinoEnabled = false;
  gameList = [];
  scopeGamesAboveThreshold = false;
  availableGames = [];

  public gamesPopOverSize: PopoverSize = 'auto';
  public bonusAmount: number;
  public totalBonus: string;
  public bonusPageSource: any;
  public allBonuses: any;
  public showForfeitConfirm: boolean = false;
  public hasBeenForfeit: boolean = false;
  public featureSiteTermsLink;
  public wager;
  public gamesThresholdToShowMoreButton: number = 5; // By default
  public hideBackButtonInNav = false;
  public isBonusScopeData: boolean = false;

  constructor(
    private appLinks: AppLinksService,
    private balanceService: AccountBalanceService,
    private bonusService: BonusService,
    private config: ConfigService,
    private events: EventsService,
    private gameHubControllerService: GameHubControllerService,
    private navigator: Navigator,
    private routing: RoutingService,
    private structureHelper: StructureHelperService,
    private successMessageService: SuccessMessageService,
  ) {
    this.successMessageService.clearMessage();

    let productVerticals = this.config.getConfig().PRODUCTVERTICALS;
    productVerticals.forEach((product) => {
      if (product.product_vertical_code == 'SPORTS') this.sportsEnabled = true;
      if (product.product_vertical_code == 'CASINO') this.casinoEnabled = true;
    });
  }

  ngOnInit() {
    this.hideBackButtonInNav = this.hideBack;

    if (!this.bonus) {
      this.onBackClicked();
    }
    this.bonusPageSource = this.source;

    this.gamesThresholdToShowMoreButton = !isNaN(Number(this.config.getConfig().FEATURE_AVAILABLE_BONUS_GAMES_NUMBER_THRESHOLD)) ? Number(this.config.getConfig().FEATURE_AVAILABLE_BONUS_GAMES_NUMBER_THRESHOLD) : 2;


    if (this.bonus?.scope.some(gameLaunchInfoList => Object.keys(gameLaunchInfoList).length > 0)) {
      this.isBonusScopeData = true;
      for (let scope of this.bonus.scope) {
        this.availableGames.push(...scope.gameLaunchInfoList); //+ ( scope.gameLaunchInfoList.length ? scope.gameLaunchInfoList.length : 0 );
      }
    } else {
      this.isBonusScopeData = false;
    }


  }

  ionViewDidEnter() {
    this.featureSiteTermsLink = this.config.getConfig().FEATURE_SITE_TERMS_LINK || '';
  }

  onShowForfeitConfirm() {
    this.events.publish("event:bonusForfeit", { forefitAction: this.bonus, forefitActionType: 'Intention' });
    this.showForfeitConfirm = true;
  }

  onCancelForfeitBonus() {
    this.events.publish("event:bonusForfeit", { forefitAction: this.bonus, forefitActionType: 'Cancel' });
    this.showForfeitConfirm = false;
  }

  onForfeitBonus() {
    this.error = null;

    this.bonusService.callForfeitBonus(this.bonus.id).subscribe(
      (response: any) => {
        if (response && !!response.result) {
          this.successMessageService.setMessage('Your bonus was forfeited.');
          this.showForfeitConfirm = false;
          this.hasBeenForfeit = true;

          this.bonusService.callGetBonusDetails(this.bonus.id).subscribe(
            (response: any) => {
              if (response && !!response.result) {
                this.events.publish("event:bonusForfeit", { forefitAction: this.bonus, forefitActionType: 'Confirm' });
                this.bonus.status = "FORFEITED";
                this.balanceService.updateBalance();
              }
            },
            (error) => {
              this.error = error;
            }
          );
        }

      },
      (error) => {
        switch (error.getErrorCode().toUpperCase()) {
          case 'CLOSE_OPENED_GAMES':
            error.setErrorMessage('Please close all opened games in order to forfeit the bonus.');
            break;
          case 'RESOLVE_SPORTS_GAME':
            error.setErrorMessage('A sport event reserved funds from this bonus - please wait for its resolution first.');
            break;
          case 'RESOLVE_SAVED_GAME':
            error.setErrorMessage('A saved game reserved funds from this bonus - please resolve it first.');
            break;
          default:
            error.setErrorMessage(error.getErrorMessage());
            break;
        }

        this.error = error;
      }
    );
  }

  onBackClicked() {
    this.navigator.back();
  }

  getBonusAmount(): string {
    let currentBonus = parseFloat(this.bonus.currentBonus) || 0;
    let currentDeposit = parseFloat(this.bonus.currentDeposit) || 0;
    let currentWinning = parseFloat(this.bonus.currentWinning) || 0;
    this.bonusAmount = currentBonus + currentDeposit + currentWinning;
    return this.bonusAmount.toFixed(2);
  }

  getProgressCasinoWidth = () => this.bonus.percentContributedCasino > 100 ? '100%' : this.bonus.percentContributedCasino + "%";
  getProgressSportsWidth = () => this.bonus.percentContributedSports > 100 ? '100%' : this.bonus.percentContributedSports + "%";

  getWagersRemaining(selectedVertical: string) {
    if (this.bonus.vertical != null) {
      return this.bonus.vertical[selectedVertical].wagersRemaining;
    }

    /**
     * let wagersRemaining;

    if (this.bonus.vertical != null) {
      let originalDeposit = ('number' == typeof this.bonus.originalDeposit) ? this.bonus.originalDeposit : 0;
      wagersRemaining = ((parseFloat(originalDeposit) + parseFloat(this.bonus.originalBonus)) * parseFloat(this.bonus.vertical[selectedVertical].rolloverWagerMultiplier)) - parseFloat(this.bonus.vertical[selectedVertical].wagerContribution);
      if (!isNaN(wagersRemaining)) {
        return this.formatNumberWithCommas(wagersRemaining.toFixed(2));
      }
    }
    return false;
    */
  }

  formatNumberWithCommas(number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  openMatchedExternalLinks(code, title) {
    return this.appLinks.openMatchedExternalLinksPopover(code, title)
  }

  getGamesList() {
    return this.gameList = [{ gameId: "123", name: "Roulette 1" }, { gameId: "123", name: "Roullete 2" }];
  }

  goBackToPromotion() {
    this.bonusService.setPromoDetailInfo({ id: this.bonus.campaignId, listType: "IN_PROGRESS" });
    this.navigator.dismissAll('');
    this.routing.toPromotionsTab();
  }

  goToLobby() {
    this.navigator.dismissAll();
    this.routing.defaultRoute();
  }

  goToAvailableGames(availableGames) {
    this.navigator.openPopover("PopoverAvailableGames", availableGames, 'standard-popover');
  }

  protected launchGameWithId(gameId) {
    this.navigator.dismissAll('');
    const gameData = this.structureHelper.getGameById(gameId);
    this.gameHubControllerService.launchCasinoGame(gameData);
  }
}
